/**
 * Hover mini-cart dropdown. Shared between the site header's basket link
 * (.deminas-header__cart) and the homepage hero's basket link
 * (.hero-industrial__cart) — see snippets/deminas-mini-cart.liquid, which
 * renders the <mini-cart> markup this file upgrades from both mount points.
 * They're template-exclusive (sections/deminas-header.liquid's own
 * `unless request.page_type == 'index'` guard), so exactly one <mini-cart>
 * instance exists on any given page — this file never assumes otherwise,
 * but relies only on `this`-scoped querySelector calls either way.
 *
 * Data freshness: the panel's contents are rendered server-side into the
 * page on load (snippets/deminas-mini-cart-contents.liquid, via
 * snippets/deminas-mini-cart.liquid) — no fetch needed for the very first
 * hover of a page view. Dawn's cart JS (assets/product-form.js,
 * assets/cart.js) publishes PUB_SUB_EVENTS.cartUpdate ('cart-update', see
 * assets/constants.js + assets/pubsub.js) after every AJAX cart mutation;
 * this file subscribes to that once, the same way
 * sections/deminas-header.liquid's own (now removed) count-refresh script
 * used to, and:
 *   - always refreshes the small visible item-count badge from `/cart.js`
 *     (cheap, so it can happen unconditionally and immediately — this is
 *     the exact fetch sections/deminas-header.liquid used to do inline);
 *   - marks the heavier panel content stale, refetching it from
 *     sections/deminas-mini-cart.liquid (Section Rendering API) the next
 *     time the panel opens, or immediately if it happens to be open
 *     already. This "lazy + invalidation flag" approach avoids re-fetching
 *     the full panel on every single cart mutation regardless of whether
 *     anyone is looking at it.
 *
 * Money: every price/subtotal string the panel shows is rendered server-side
 * by snippets/deminas-mini-cart-contents.liquid using Liquid's `money` /
 * `money_with_currency` filters. This file does no currency math and never
 * hardcodes a currency symbol — same discipline as assets/cart-notification.js.
 *
 * Touch/coarse-pointer devices: mouseenter/mouseleave listeners are only
 * attached when `matchMedia('(hover: hover) and (pointer: fine)')` matches.
 * On a touch device that check fails, so this file never wires hover at
 * all — the trigger stays a plain `<a href="/cart">` and a tap just
 * navigates, exactly like any other link. Keyboard focus handling is wired
 * unconditionally (below) so keyboard users get the dropdown regardless of
 * pointer type.
 *
 * Hard constraint: PUB_SUB_EVENTS.cartUpdate subscribers must never throw —
 * a throw rejects the publish() Promise.all chain for every subscriber,
 * including assets/cart-notification.js's own, silently breaking the
 * add-to-cart popup. Every DOM lookup below is guarded accordingly.
 */
(function () {
  'use strict';

  var CLOSE_DELAY = 200;
  var SECTION_ID = 'deminas-mini-cart';

  function fineHoverSupported() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches
    );
  }

  function getCartUrl() {
    if (typeof routes !== 'undefined' && routes && routes.cart_url) return routes.cart_url;
    return '/cart';
  }

  function getSectionInnerHTML(html, selector) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var el = doc.querySelector(selector || '.shopify-section');
      return el ? el.innerHTML : '';
    } catch (e) {
      return '';
    }
  }

  class MiniCartElement extends HTMLElement {
    constructor() {
      super();

      this.trigger = this.querySelector('.deminas-mini-cart__trigger');
      this.panel = this.querySelector('.deminas-mini-cart__panel');
      this.body = this.querySelector('.deminas-mini-cart__panel-inner');

      if (!this.trigger || !this.panel || !this.body) return;

      this.closeTimer = null;
      this.loading = false;
      // The panel already holds the real, current basket from the initial
      // server render (see snippets/deminas-mini-cart.liquid), so it starts
      // "fresh" — no fetch is needed until something changes it.
      this.stale = false;

      if (fineHoverSupported()) {
        this.addEventListener('mouseenter', this.handleOpenIntent.bind(this));
        this.addEventListener('mouseleave', this.handleCloseIntent.bind(this));
      }

      // Keyboard support is unconditional — wired regardless of pointer
      // type, so a keyboard (or switch control) user on any device can
      // reach the panel. focusin/focusout bubble (unlike focus/blur), so
      // binding once here covers the trigger and, once open, every control
      // inside the panel too.
      this.addEventListener('focusin', this.handleOpen.bind(this));
      this.addEventListener('focusout', this.handleFocusOut.bind(this));
      this.addEventListener('keydown', this.handleKeydown.bind(this));

      if (typeof subscribe === 'function') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
      }
    }

    // ---- open/close ----

    handleOpenIntent() {
      this.clearCloseTimer();
      this.open();
    }

    handleCloseIntent() {
      this.clearCloseTimer();
      var self = this;
      this.closeTimer = window.setTimeout(function () {
        self.close();
      }, CLOSE_DELAY);
    }

    clearCloseTimer() {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
    }

    open() {
      this.clearCloseTimer();
      this.classList.add('is-open');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
      this.refreshPanelIfNeeded();
    }

    // focusin fires on every focus movement within the component (trigger,
    // then later the panel's own links/button) — always (re)open rather
    // than trying to distinguish "first" focus, it's idempotent either way.
    handleOpen() {
      this.open();
    }

    close() {
      this.classList.remove('is-open');
      if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
    }

    handleFocusOut(event) {
      // Only close if focus is leaving the whole group, not moving between
      // two elements inside it (trigger -> checkout button, etc.).
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.handleCloseIntent();
    }

    handleKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        this.close();
        if (this.trigger) this.trigger.focus();
      }
    }

    // ---- data freshness ----

    handleCartUpdate() {
      // Guarded top-to-bottom: this runs inside pubsub.js's publish()
      // Promise.all chain, and must never throw (see file header comment).
      try {
        this.refreshCount();
        this.stale = true;
        if (this.classList.contains('is-open')) {
          this.refreshPanelIfNeeded();
        }
      } catch (e) {
        // Swallow — a broken mini-cart refresh must never break the
        // add-to-cart popup or any other cartUpdate subscriber.
      }
    }

    refreshCount() {
      var countEl = this.querySelector ? this.querySelector('.deminas-mini-cart__count') : null;
      if (!countEl) return;
      fetch(getCartUrl() + '.js')
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          if (cart && typeof cart.item_count !== 'undefined') {
            countEl.textContent = cart.item_count;
          }
        })
        .catch(function () {
          // Leave the last-known count in place on a failed fetch.
        });
    }

    refreshPanelIfNeeded() {
      if (!this.body || this.loading || !this.stale) return;
      this.loading = true;
      var self = this;

      fetch(getCartUrl() + '?section_id=' + SECTION_ID)
        .then(function (response) { return response.text(); })
        .then(function (html) {
          var inner = getSectionInnerHTML(html, '.shopify-section');
          if (!inner || !self.body) return;
          self.body.innerHTML = inner;
          self.stale = false;
        })
        .catch(function () {
          // Leave whatever is already rendered in place on a failed
          // refresh rather than surfacing an error in the panel.
        })
        .finally(function () {
          self.loading = false;
        });
    }
  }

  if (!customElements.get('mini-cart')) {
    customElements.define('mini-cart', MiniCartElement);
  }
})();
